import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Spin, message } from "antd";
import type { AxiosError } from "axios";

import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
} from "../../../../utils/ErrorUtils";

import "../EventMarkets/stylesEventMarkets.css";
import "./stylesFutures.css";

import FutureMarketTabs from "./FutureMarketTabs";
import FutureMarketCards from "./FutureMarketCards";
import FuturesSportSelect from "./FuturesSportSelect";
import FuturesLeagueSearch from "./FuturesLeagueSearch";
import {
  mapRawSportsToSports,
  parseTokenFromStorage,
} from "../EventMarkets/eventMarketsTransforms";
import {
  ENTITY_TYPES,
  type RawSport,
  type Sport,
} from "../EventMarkets/eventMarketsTypes";
import {
  mapRawFutureMarketOptions,
  mapRawFuturesItems,
  mapRawFuturesLeagues,
  type FuturesListData,
  type League,
  type RawFutureMarketAutocompleteItem,
  type RawFutureTabItem,
  type RawFuturesLeagueItem,
} from "./futuresTransforms";
import { useFuturesDummyState } from "./useFuturesDummyState";
import type { BundledTranslationChanges } from "../Translations/RenameEntityModal";
import { useProductFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { TRIVELTA_PRODUCTS } from "../../../../utils/products";
import type {
  FutureCard,
  FutureMarketOption,
  FutureTab,
} from "./futuresDummyData";

type ApiError = AxiosError<{ message?: string } | undefined>;
type PrivateAdminApi = ReturnType<typeof createPrivateAdminPanelInstance>;
type ApiMessageResponse = { message?: string } | undefined;
type CreateFuturesTabResponse = {
  message?: string;
  data?: RawFutureTabItem;
} | undefined;
type FutureMarketPayload = { market_id: string; specifier?: string };

// Cards and market options share the same shape needed by the futures update
// API: the numeric UOF market id plus an optional specifier. The specifier key
// is omitted (not sent as undefined) when the market has none.
function toMarketPayload(market: {
  market_id: number;
  specifier?: string;
}): FutureMarketPayload {
  return {
    market_id: String(market.market_id),
    ...(market.specifier ? { specifier: market.specifier } : {}),
  };
}

// The sports endpoint has no FUTURES entity type yet; PREMATCH matches what
// TopLeaguesPerSport sends for its non-event sports list. Swap this constant
// once the backend exposes a futures-specific entity type.
const FUTURES_SPORTS_ENTITY_TYPE = ENTITY_TYPES.PREMATCH;

// These mutations only toast on success when the backend sent copy, so the
// extractor runs with an empty fallback rather than an AppStrings key.
function showBackendSuccess(response: unknown): void {
  const text = getApiSuccessMessage(response, "");
  if (text) message.success(text);
}

function Futures(): React.ReactElement {
  const { isEnabled: translationsFlagEnabled } = useProductFeatureFlag(
    "fm_translations",
    TRIVELTA_PRODUCTS.SPORTSBOOK
  );
  const translationsEnabled = translationsFlagEnabled === true;
  const token = parseTokenFromStorage(localStorage.getItem("token"));

  const api = useMemo<PrivateAdminApi>(
    () => createPrivateAdminPanelInstance(token?.AccessToken),
    [token?.AccessToken]
  );

  const [activeSportId, setActiveSportId] = useState<Sport["id"] | null>(null);
  const [activeLeague, setActiveLeague] = useState<League | null>(null);
  const activeLeagueId = activeLeague?.id ?? null;
  // Local filter term for the league combobox (list comes from GET_FUTURES_LEAGUES).
  const [leagueSearchTerm, setLeagueSearchTerm] = useState("");
  // The market autocomplete list is fetched lazily the first time an Add
  // Market modal is opened, then cached per league for local searching.
  const [isAddCardOpen, setIsAddCardOpen] = useState(false);

  // Sports list (top row)
  const sportsQuery = useQuery<Sport[], ApiError>({
    queryKey: ["futures", "sports"],
    queryFn: async () => {
      const res = await api.post(API_ROUTES.GET_EVENT_MARKETS_SPORTS, {
        entity_type: FUTURES_SPORTS_ENTITY_TYPE,
      });
      const sports = (res?.data?.sports || []) as RawSport[];
      return mapRawSportsToSports(sports);
    },
    refetchOnWindowFocus: false,
  });

  // set default active sport
  useEffect(() => {
    if (!sportsQuery.isLoading && sportsQuery.data && sportsQuery.data.length) {
      if (
        !activeSportId ||
        !sportsQuery.data.find((s) => s.id === activeSportId)
      ) {
        setActiveSportId(sportsQuery.data[0].id);
      }
    }
  }, [sportsQuery.isLoading, sportsQuery.data, activeSportId]);

  // Never keep a league selected from the previous sport.
  useEffect(() => {
    setActiveLeague(null);
    setLeagueSearchTerm("");
  }, [activeSportId]);

  // Leagues for the active sport (futures-specific list endpoint).
  const leaguesQuery = useQuery<League[], ApiError>({
    queryKey: ["futures", "leagues", activeSportId],
    enabled: !!activeSportId,
    queryFn: async () => {
      const res = await api.get(
        API_ROUTES.GET_FUTURES_LEAGUES.replace(
          "{sport_id}",
          String(activeSportId)
        )
      );
      const raw = res?.data?.data;
      const items = (
        Array.isArray(raw) ? raw : raw?.items || []
      ) as RawFuturesLeagueItem[];
      return mapRawFuturesLeagues(items);
    },
    refetchOnWindowFocus: false,
  });

  const filteredLeagues = useMemo(() => {
    const leagues = leaguesQuery.data ?? [];
    const term = leagueSearchTerm.trim().toLowerCase();
    if (!term) return leagues;
    return leagues.filter(
      (league) =>
        league.name.toLowerCase().includes(term) ||
        (league.country_name || "").toLowerCase().includes(term)
    );
  }, [leaguesQuery.data, leagueSearchTerm]);

  // Auto-pick the first league when none is selected yet (sport load /
  // sport change). Search picks are explicit via the combobox.
  useEffect(() => {
    if (activeLeague) return;
    if (!leaguesQuery.isLoading && leaguesQuery.data?.length) {
      setActiveLeague(leaguesQuery.data[0]);
    }
  }, [activeLeague, leaguesQuery.isLoading, leaguesQuery.data]);

  // Tabs + markets for the active sport/league
  const futuresQuery = useQuery<FuturesListData, ApiError>({
    queryKey: ["futures", "tabs", activeSportId, activeLeagueId],
    enabled: !!activeSportId && !!activeLeagueId,
    queryFn: async () => {
      const res = await api.get(
        API_ROUTES.GET_FUTURES_ENTITIES.replace(
          "{sport_id}",
          String(activeSportId)
        ).replace("{league_id}", String(activeLeagueId))
      );
      const items = (res?.data?.data?.items || []) as RawFutureTabItem[];
      return mapRawFuturesItems(items);
    },
    refetchOnWindowFocus: false,
  });

  const futuresState = useFuturesDummyState(
    activeLeagueId,
    futuresQuery.data ?? null,
    futuresQuery.dataUpdatedAt
  );

  // All markets for the active league, used to populate the Add Market
  // dropdown. Fetched once per league (lazily) and filtered client-side.
  const marketOptionsQuery = useQuery<FutureMarketOption[], ApiError>({
    queryKey: ["futures", "marketOptions", activeSportId, activeLeagueId],
    enabled: isAddCardOpen && !!activeSportId && !!activeLeagueId,
    queryFn: async () => {
      const res = await api.get(
        API_ROUTES.GET_FUTURES_MARKETS_AUTOCOMPLETE.replace(
          "{sport_id}",
          String(activeSportId)
        ).replace("{league_id}", String(activeLeagueId))
      );
      const items = (res?.data?.data || []) as RawFutureMarketAutocompleteItem[];
      return mapRawFutureMarketOptions(items);
    },
    refetchOnWindowFocus: false,
  });

  const {
    addTab,
    renameTab,
    setTabActive,
    reorderTabs,
    deleteTab,
    addCard,
    deleteCard,
    reorderCards,
    setTabCards,
  } = futuresState;

  // Persist the tab order. Payload is every tab's backend id in the new order.
  const reorderTabsMutation = useMutation<
    ApiMessageResponse,
    ApiError,
    { orderedIds: string[]; previousTabs: FutureTab[] }
  >({
    mutationFn: async ({ orderedIds }) => {
      const res = await api.patch(API_ROUTES.ORDER_FUTURES_ENTITIES, {
        ordered_ids: orderedIds,
      });
      return res.data;
    },
    onSuccess: (data) => {
      // The optimistic local reorder already reflects the new order, so we
      // deliberately skip a refetch here to avoid reloading the whole page.
      showBackendSuccess(data);
    },
    onError: (error, variables) => {
      reorderTabs(variables.previousTabs);
      message.error(
        getApiErrorMessage(error, AppStrings.FAILED_TO_UPDATE_TAB_ORDER)
      );
    },
  });

  // Which tab's active toggle is in flight (drives the row's Switch spinner).
  const [togglingTabId, setTogglingTabId] = useState<string | null>(null);

  // Create a tab via POST, then append it locally — no list refetch.
  const createTabMutation = useMutation<
    CreateFuturesTabResponse,
    ApiError,
    string
  >({
    mutationFn: async (displayName: string) => {
      const res = await api.post(API_ROUTES.CREATE_FUTURES_ENTITY, {
        sport_id: String(activeSportId),
        league_id: String(activeLeagueId),
        display_name: displayName,
      });
      return res.data;
    },
    onSuccess: (data, displayName) => {
      showBackendSuccess(data);
      const item = data?.data;
      const id =
        item?.item_id ||
        item?.entity_id ||
        displayName.toLowerCase().replace(/\s+/g, "-");
      addTab({
        id,
        ename: item?.display_name || displayName,
        // Empty order lets addTab assign the next index from current state.
        order:
          item?.display_order != null ? String(item.display_order) : "",
        entity_id: item?.entity_id,
        translations: item?.translations || null,
      });
    },
    onError: (error) => {
      message.error(
        getApiErrorMessage(error, AppStrings.FAILED_TO_CREATE_TAB)
      );
    },
  });

  const handleAddTab = useCallback(
    async (name: string) => {
      await createTabMutation.mutateAsync(name);
    },
    [createTabMutation]
  );

  // Rename a tab via PATCH, then update it locally — no list refetch.
  const editTabMutation = useMutation<
    ApiMessageResponse,
    ApiError,
    {
      entityId: string;
      displayName: string;
      translationChanges?: BundledTranslationChanges;
    }
  >({
    mutationFn: async ({ entityId, displayName, translationChanges }) => {
      const res = await api.patch(API_ROUTES.UPDATE_FUTURES_ENTITY, {
        entity_id: entityId,
        display_name: displayName,
        ...(translationChanges
          ? {
              translation_key: translationChanges.translation_key,
              translations: translationChanges.translations,
            }
          : {}),
      });
      return res.data;
    },
    onSuccess: (data) => {
      showBackendSuccess(data);
    },
    onError: (error) => {
      message.error(
        getApiErrorMessage(error, AppStrings.FAILED_TO_UPDATE_TAB)
      );
    },
  });

  // Toggle a tab's active flag via PATCH. The switch is updated optimistically
  // and reverted if the request fails.
  const toggleTabMutation = useMutation<
    ApiMessageResponse,
    ApiError,
    { tabId: string; entityId: string; isActive: boolean }
  >({
    mutationFn: async ({ entityId, isActive }) => {
      const res = await api.patch(API_ROUTES.UPDATE_FUTURES_ENTITY, {
        entity_id: entityId,
        is_active: isActive,
      });
      return res.data;
    },
    onSuccess: (data) => {
      showBackendSuccess(data);
    },
    onError: (error, variables) => {
      // Revert the optimistic flip.
      setTabActive(variables.tabId, !variables.isActive);
      message.error(
        getApiErrorMessage(error, AppStrings.FAILED_TO_UPDATE_TAB)
      );
    },
    onSettled: () => {
      setTogglingTabId(null);
    },
  });

  // Delete a tab via DELETE, then drop it locally — no list refetch.
  const deleteTabMutation = useMutation<ApiMessageResponse, ApiError, string>({
    mutationFn: async (entityId: string) => {
      const res = await api.delete(API_ROUTES.DELETE_FUTURES_ENTITY, {
        data: { entity_id: entityId },
      });
      return res.data;
    },
    onSuccess: (data) => {
      showBackendSuccess(data);
    },
    onError: (error) => {
      message.error(
        getApiErrorMessage(error, AppStrings.FAILED_TO_DELETE_TAB)
      );
    },
  });

  const { tabs: currentTabs } = futuresState;

  const handleDeleteTab = useCallback(
    async (tabId: string) => {
      const tab = currentTabs.find((t) => t.id === tabId);
      const entityId = tab?.entity_id;
      // Locally seeded tabs without a backend id can only be removed client-side.
      if (entityId) {
        await deleteTabMutation.mutateAsync(entityId);
      }
      deleteTab(tabId);
    },
    [currentTabs, deleteTab, deleteTabMutation]
  );

  const handleEditTab = useCallback(
    async (
      tabId: string,
      newName: string,
      translationChanges?: BundledTranslationChanges
    ) => {
      const tab = currentTabs.find((t) => t.id === tabId);
      const entityId = tab?.entity_id;
      // Locally seeded tabs without a backend id can only be renamed client-side.
      if (entityId) {
        await editTabMutation.mutateAsync({
          entityId,
          displayName: newName,
          translationChanges,
        });
      }
      renameTab(tabId, newName, translationsEnabled, translationChanges);
    },
    [currentTabs, renameTab, editTabMutation, translationsEnabled]
  );

  const handleToggleTab = useCallback(
    (tabId: string, isActive: boolean) => {
      const tab = currentTabs.find((t) => t.id === tabId);
      const entityId = tab?.entity_id;
      // Optimistically flip the switch; the mutation reverts on failure.
      setTabActive(tabId, isActive);
      // Locally seeded tabs without a backend id stay client-side only.
      if (entityId) {
        setTogglingTabId(tabId);
        toggleTabMutation.mutate({ tabId, entityId, isActive });
      }
    },
    [currentTabs, setTabActive, toggleTabMutation]
  );

  const handleReorderTabs = useCallback(
    (reordered: FutureTab[]) => {
      const previousTabs = currentTabs;
      // Optimistic local update so the row order sticks while the API runs.
      reorderTabs(reordered);
      const orderedIds = reordered.map((tab) => tab.entity_id || tab.id);
      if (orderedIds.length) {
        reorderTabsMutation.mutate({ orderedIds, previousTabs });
      }
    },
    [currentTabs, reorderTabs, reorderTabsMutation]
  );

  // Persist a tab's market order. The backend replaces the tab's market list
  // with exactly what we send, so the payload is every market in the new order.
  const reorderCardsMutation = useMutation<
    ApiMessageResponse,
    ApiError,
    {
      tabId: string;
      entityId: string;
      markets: FutureMarketPayload[];
      previousOrder: string[];
    }
  >({
    mutationFn: async ({ entityId, markets }) => {
      const res = await api.patch(API_ROUTES.UPDATE_FUTURES_ENTITY, {
        entity_id: entityId,
        markets,
      });
      return res.data;
    },
    onSuccess: (data) => {
      showBackendSuccess(data);
    },
    onError: (error, variables) => {
      // Restore the previous card order.
      reorderCards(variables.tabId, variables.previousOrder);
      message.error(
        getApiErrorMessage(error, AppStrings.FAILED_TO_UPDATE_MARKET_ORDER)
      );
    },
  });

  const { cardsByTabId } = futuresState;

  const handleReorderCards = useCallback(
    (tabId: string, orderedEntityIds: string[]) => {
      const currentCards = cardsByTabId[tabId] ?? [];
      const previousOrder = currentCards.map((card) => card.entity_id);
      // Optimistic local reorder so the rows stick while the API runs.
      reorderCards(tabId, orderedEntityIds);

      const tab = currentTabs.find((t) => t.id === tabId);
      const entityId = tab?.entity_id;
      // Locally seeded tabs without a backend id stay client-side only.
      if (!entityId) return;

      const byId = new Map(
        currentCards.map((card) => [card.entity_id, card])
      );
      const markets: FutureMarketPayload[] = orderedEntityIds
        .map((id) => byId.get(id))
        .filter((card): card is FutureCard => Boolean(card))
        .map(toMarketPayload);

      if (markets.length) {
        reorderCardsMutation.mutate({
          tabId,
          entityId,
          markets,
          previousOrder,
        });
      }
    },
    [cardsByTabId, currentTabs, reorderCards, reorderCardsMutation]
  );

  // Add a market to a tab. The update API replaces the tab's whole market list,
  // so the payload is every existing market plus the new one appended last.
  const addCardMutation = useMutation<
    ApiMessageResponse,
    ApiError,
    {
      tabId: string;
      entityId: string;
      markets: FutureMarketPayload[];
      previousCards: FutureCard[];
    }
  >({
    mutationFn: async ({ entityId, markets }) => {
      const res = await api.patch(API_ROUTES.UPDATE_FUTURES_ENTITY, {
        entity_id: entityId,
        markets,
      });
      return res.data;
    },
    onSuccess: (data) => {
      showBackendSuccess(data);
    },
    onError: (error, variables) => {
      // Drop the optimistically added card by restoring the prior snapshot.
      setTabCards(variables.tabId, variables.previousCards);
      message.error(
        getApiErrorMessage(error, AppStrings.FAILED_TO_ADD_MARKET)
      );
    },
  });

  const handleAddCard = useCallback(
    (tabId: string, options: FutureMarketOption[]) => {
      if (options.length === 0) return;
      const previousCards = cardsByTabId[tabId] ?? [];
      // Optimistically append every selected market so the rows show while the
      // API runs.
      options.forEach((option) => addCard(tabId, option));

      const tab = currentTabs.find((t) => t.id === tabId);
      const entityId = tab?.entity_id;
      // Locally seeded tabs without a backend id stay client-side only.
      if (!entityId) return;

      const markets: FutureMarketPayload[] = [
        ...previousCards.map(toMarketPayload),
        ...options.map(toMarketPayload),
      ];
      addCardMutation.mutate({ tabId, entityId, markets, previousCards });
    },
    [cardsByTabId, currentTabs, addCard, addCardMutation]
  );

  // Delete a market from a tab. The update API replaces the tab's whole market
  // list, so the payload is every remaining market (the selected one removed).
  const deleteCardMutation = useMutation<
    ApiMessageResponse,
    ApiError,
    {
      tabId: string;
      entityId: string;
      markets: FutureMarketPayload[];
      previousCards: FutureCard[];
    }
  >({
    mutationFn: async ({ entityId, markets }) => {
      const res = await api.patch(API_ROUTES.UPDATE_FUTURES_ENTITY, {
        entity_id: entityId,
        markets,
      });
      return res.data;
    },
    onSuccess: (data) => {
      showBackendSuccess(data);
    },
    onError: (error, variables) => {
      // Restore the removed card by reverting to the prior snapshot.
      setTabCards(variables.tabId, variables.previousCards);
      message.error(
        getApiErrorMessage(error, AppStrings.FAILED_TO_DELETE_MARKET)
      );
    },
  });

  const handleDeleteCard = useCallback(
    (tabId: string, entityId: string) => {
      // The update API replaces the whole market list — overlapping deletes
      // on the same tab can restore a card the earlier request already removed.
      if (deleteCardMutation.isPending) return;

      const previousCards = cardsByTabId[tabId] ?? [];
      // Optimistically drop the row while the API runs.
      deleteCard(tabId, entityId);

      const tab = currentTabs.find((t) => t.id === tabId);
      const tabEntityId = tab?.entity_id;
      // Locally seeded tabs without a backend id stay client-side only.
      if (!tabEntityId) return;

      const markets: FutureMarketPayload[] = previousCards
        .filter((card) => card.entity_id !== entityId)
        .map(toMarketPayload);
      deleteCardMutation.mutate({
        tabId,
        entityId: tabEntityId,
        markets,
        previousCards,
      });
    },
    [cardsByTabId, currentTabs, deleteCard, deleteCardMutation]
  );

  const sportsLoading = sportsQuery.isLoading;
  // Prefer a spinner only when there is nothing to show yet; keep prior
  // options visible while a search refetch is in flight.
  const leaguesLoading =
    leaguesQuery.isLoading ||
    (leaguesQuery.isFetching && !(leaguesQuery.data?.length));
  const futuresLoading = futuresQuery.isLoading || futuresQuery.isFetching;

  return (
    <div className="futures" data-testid="futures-tab-content">
      <div className="futures-filters" data-testid="futures-filters">
        <FuturesSportSelect
          sports={sportsQuery.isError ? [] : sportsQuery.data ?? []}
          selectedId={activeSportId}
          onSelect={setActiveSportId}
          isLoading={sportsLoading}
          isError={sportsQuery.isError}
        />
        <FuturesLeagueSearch
          selected={activeLeague}
          options={activeSportId ? filteredLeagues : []}
          isLoading={!!activeSportId && leaguesLoading}
          isError={!!activeSportId && leaguesQuery.isError}
          onSelect={setActiveLeague}
          onSearchChange={setLeagueSearchTerm}
        />
      </div>

      {activeSportId && activeLeagueId && (
        <>
          {futuresLoading ? (
            <div
              className="futures-tabs-loading"
              data-testid="futures-tabs-loading"
            >
              <Spin size="small" />
            </div>
          ) : futuresQuery.isError ? (
            <p
              className="futures-tabs-error"
              data-testid="futures-tabs-error"
            >
              {AppStrings.FAILED_TO_LOAD_FUTURE_MARKET_TABS}
            </p>
          ) : (
            <>
              <FutureMarketTabs
                tabs={futuresState.tabs}
                onAddTab={handleAddTab}
                onEditTab={handleEditTab}
                onToggleTab={handleToggleTab}
                onDeleteTab={handleDeleteTab}
                onReorderTabs={handleReorderTabs}
                isAddingTab={createTabMutation.isPending}
                isEditingTab={editTabMutation.isPending}
                isDeletingTab={deleteTabMutation.isPending}
                togglingTabId={togglingTabId}
              />
              <FutureMarketCards
                tabs={futuresState.tabs}
                cardsByTabId={futuresState.cardsByTabId}
                onAddCard={handleAddCard}
                onDeleteCard={handleDeleteCard}
                onReorderCards={handleReorderCards}
                marketOptions={marketOptionsQuery.data ?? []}
                isMarketOptionsLoading={
                  marketOptionsQuery.isLoading || marketOptionsQuery.isFetching
                }
                isMarketOptionsError={marketOptionsQuery.isError}
                onAddCardOpenChange={setIsAddCardOpen}
                isDeletingCard={deleteCardMutation.isPending}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

export default Futures;
